import {
    HttpErrorResponse,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AuthService } from '#root/core/auth/auth.service';
import { AuthUtils } from '#root/core/auth/auth.utils';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    private _isRefreshing = false;
    private _refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    /**
     * Constructor
     */
    constructor(private readonly _authService: AuthService) {
    }

    /**
     * Intercept
     *
     * @param req
     * @param next
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        // Skip interceptor for auth endpoints to avoid infinite loops
        const isAuthEndpoint =
            req.url.includes('User/Login') ||
            req.url.includes('User/RefreshToken') ||
            req.url.includes('User/Logout') ||
            req.url.includes('Customer/Login') ||
            req.url.includes('Customer/RefreshToken');

        if (isAuthEndpoint) {
            return next.handle(req);
        }

        // If the token is expired but we have a refresh token, attempt to refresh first
        if (AuthUtils.isTokenExpired(this._authService.accessToken) && this._authService.refreshToken) {
            // If already refreshing, wait for the result and retry with new token
            if (this._isRefreshing) {
                return this._refreshTokenSubject.pipe(
                    filter(token => token !== null),
                    take(1),
                    switchMap((token) => {
                        return next.handle(this._addAuthHeader(req, token));
                    })
                );
            }

            // First request to detect expired token — start refresh
            this._isRefreshing = true;
            this._refreshTokenSubject.next(null);

            return this._authService.refreshUserToken().pipe(
                switchMap((refreshed) => {
                    this._isRefreshing = false;
                    if (refreshed) {
                        const newToken = this._authService.accessToken;
                        this._refreshTokenSubject.next(newToken);
                        return next.handle(this._addAuthHeader(req, newToken));
                    }
                    // Refresh failed — sign out
                    this._authService.signOut();
                    location.reload();
                    return throwError(() => new Error('Session expired'));
                }),
                catchError((err) => {
                    this._isRefreshing = false;
                    this._authService.signOut();
                    location.reload();
                    return throwError(() => new Error('Session expired'));
                })
            );
        }

        // Add Authorization header if token is still valid
        let newReq = req.clone();
        if (this._authService.accessToken && !AuthUtils.isTokenExpired(this._authService.accessToken)) {
            newReq = this._addAuthHeader(req, this._authService.accessToken);
        }

        // Response — handle 401 as fallback (e.g. token invalidated server-side)
        return next.handle(newReq).pipe(
            catchError((error) => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    // If we have a refresh token and aren't already refreshing, try refresh on 401
                    if (this._authService.refreshToken && !this._isRefreshing) {
                        this._isRefreshing = true;
                        this._refreshTokenSubject.next(null);

                        return this._authService.refreshUserToken().pipe(
                            switchMap((refreshed) => {
                                this._isRefreshing = false;
                                if (refreshed) {
                                    const newToken = this._authService.accessToken;
                                    this._refreshTokenSubject.next(newToken);
                                    return next.handle(this._addAuthHeader(req, newToken));
                                }
                                this._authService.signOut();
                                location.reload();
                                return throwError(() => error);
                            }),
                            catchError(() => {
                                this._isRefreshing = false;
                                this._authService.signOut();
                                location.reload();
                                return throwError(() => error);
                            })
                        );
                    }

                    // Refresh already in progress — wait for it to complete, then retry
                    if (this._isRefreshing) {
                        return this._refreshTokenSubject.pipe(
                            filter(token => token !== null),
                            take(1),
                            switchMap(token => next.handle(this._addAuthHeader(req, token)))
                        );
                    }

                    this._authService.signOut();
                    location.reload();
                }
                return throwError(() => error);
            })
        );
    }

    private _addAuthHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
        return req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + token),
        });
    }
}